import Vue from "vue";
import set from "lodash/set";
import { get } from "@/utils";
import deepmerge from "deepmerge";
import { _isMobile } from "../utils/helpers";
import { getEnvironment } from "../../utils";

const AdobeFunctions = {
  internal(context, _callback) {
    AdobeFunctions.setOptimizations(context);
    AdobeFunctions.setTypology(context);
    setTimeout(_callback, 0);
  },
  callAdobeDTM(method, event) {
    /**
     * Realizar llamada al DTM
     */
    if (event !== "Page View") {
      // No se debe reportar los props de application para page views
      AdobeFunctions.setApplicationProps();
    }
    if (event === "App Page Visit") {
      set(window.digitalData, "application.isQualifiedVisits", "true");
    } else {
      set(window.digitalData, "application.isQualifiedVisits", "");
    }
    try {
      window[method](event);
    } catch (error) {
      Vue.$log.error("🚀 Adobe Launch error: ", error);
    }
    Vue.$log.info("🚀 Adobe Launch call: ", event);
  },
  clearVars() {
    /**
     * Limpiar las variables cuando hace falta
     */
    set(window.digitalData, "products.attributes", []);
    set(window.digitalData, "application.state", "");
    set(window.digitalData, "application.step", "");
    set(window.digitalData, "application.isQualifiedVisits", "");
    set(window.digitalData, "application.globalApplication", "");
    set(window.digitalData, "application.errorType", "");
    set(window.digitalData, "page.pageInfo.errorPage", "");
    set(window.digitalData, "page.pageInfo.level2", "");
    set(window.digitalData, "page.pageInfo.pageName", "");
    set(
      window.digitalData,
      "application",
      deepmerge(window.digitalData.application, {
        application: {
          type: "",
          name: ""
        },
        fulfillmentModel: ""
      })
    );
  },
  setPageInstanceId() {
    /**
     * Establecer entorno en datalayer
     */
    const environment = getEnvironment();
    if (environment !== null) {
      set(window.digitalData, "pageInstanceID", environment.name);
      return;
    }
    set(window.digitalData, "pageInstanceID", "pro");
  },
  setIsMobile() {
    if (_isMobile()) {
      set(window.digitalData, "user.device.mobile", "si");
    } else {
      set(window.digitalData, "user.device.mobile", "no");
    }
  },
  setSysEnv() {
    if (_isMobile()) {
      set(window.digitalData, "page.pageInfo.sysEnv", "web-movil");
    } else {
      set(window.digitalData, "page.pageInfo.sysEnv", "escritorio");
    }
  },
  setPageName(pageName) {
    let segments = [
      get(window.digitalData, "page.pageInfo.sysEnv", "escritorio"),
      "publica",
      "general",
      "cobrar jubilacion"
    ];
    if (pageName) {
      segments.push(pageName);
    }
    set(window.digitalData, "page.pageInfo.pageName", segments.join(":"));
  },
  setConstants() {
    set(window.digitalData, "page.pageInfo.server", window.location.hostname);
    set(window.digitalData, "user.device.userAgent", navigator.userAgent);
  },
  setOptimizations(context) {
    if (context?.state?.adobeTargetExperience) {
      /**
       * Se especificó una experiencia de Target. Indicarla al tagging.
       * La experiencia DEBE venir de Target con el formato: `IDACTIVIDAD_EXPERIENCIA`
       * Ej:"AR072019_B"
       */
      let datosTarget = context.state.adobeTargetExperience.split("_");
      if (datosTarget.length === 2) {
        let attributes = [
          {
            idOptimization: datosTarget[0],
            experience: datosTarget[1],
            place: "",
            type: "",
            executor: "Target",
            audience: ""
          }
        ];
        set(window.digitalData, "optimization.attributes", attributes);
      }
    }
  },
  setTypology(/*context*/) {},
  setInternalCampaign() {
    let attributes = [
      {
        location: "",
        campaignFormat: "",
        collectiveCode: "",
        campaignName: "",
        product: "",
        productCode: "",
        quantity: ""
      }
    ];
    set(window.digitalData, "internalCampaign.attributes", attributes);
  },
  setProductAttributes() {
    let attributes = [];
    let attributesModel = {
      primaryCategory: "",
      productSubtype: "",
      productName: "",
      productCode: "",
      quantity: "",
      amount: "",
      paymentAmount: "",
      numberOfPayments: "",
      paymentDate: "",
      paymentType: "",
      serviceCharge: "",
      currency: "",
      numberOfHolders: "",
      interestRate: {
        tin: "",
        tae: "",
        rate: ""
      },
      term: "",
      group: "",
      state: ""
    };

    attributes.push(
      deepmerge(attributesModel, {
        primaryCategory: "servicios",
        productName: "adquirencia clientes"
      })
    );

    set(window.digitalData, "products.attributes", attributes);
  },
  setProductState(state) {
    get(window.digitalData, "products.attributes", []).forEach(
      (product, index) => {
        set(window.digitalData.products.attributes, `[${index}].state`, state);
      }
    );
  },
  setApplicationProps() {
    set(
      window.digitalData,
      "application",
      deepmerge(window.digitalData.application, {
        application: {
          type: "formulario",
          name: "cobrar jubilacion"
        },
        fulfillmentModel: "online",
        globalApplication: "collect retirement"
      })
    );
  },
  setOperationNumber(operationNumber) {
    set(window.digitalData, "application.operationNumber", operationNumber);
  }
};

const Adobe = {
  enable() {},
  wait() {
    return !!window._satellite && !!(document.readyState === "complete");
  },
  init() {
    AdobeFunctions.internal(undefined, () => {
      Vue.$log.info("🚀 Adobe Launch working");
      AdobeFunctions.setConstants();
      AdobeFunctions.setSysEnv();
      AdobeFunctions.setPageInstanceId();
      AdobeFunctions.setIsMobile();
    });
  },
  clickTracking(context) {
    AdobeFunctions.internal(context, () => {
      set(window.digitalData, "application.step", context.mutation.payload);
      console.log(context);
      //AdobeFunctions.setDoTrackingClick(context.mutation.payload);
      AdobeFunctions.setProductAttributes();
      AdobeFunctions.callAdobeDTM("digitalLink", "App Started");
    });
  },
  clickCTA(context) {
    AdobeFunctions.internal(context, () => {
      set(
        window.digitalData,
        "page.pageActivity.link.name",
        context.mutation.payload
      );
      AdobeFunctions.setProductAttributes();
      AdobeFunctions.callAdobeDTM("digitalLink", "interno");
    });
  },
  // landing(/*context*/) {},
  mensaje(context) {
    AdobeFunctions.internal(context, () => {
      // Para los errores se disparan eventos diferentes
      // de acuerdo al error por lo que en cada case debe llamarse a adobeDTM
      AdobeFunctions.clearVars();
      switch (context.mutation.payload.params.mje) {
        default:
          set(window.digitalData, "page.pageInfo.level2", "error tecnico");
          set(window.digitalData, "page.pageInfo.errorPage", "error tecnico");
          AdobeFunctions.setPageName("error tecnico");
          AdobeFunctions.callAdobeDTM("digitalView", "Page View");
          break;
      }
    });
  },
  paso1(context) {
    AdobeFunctions.internal(context, () => {
      AdobeFunctions.clearVars();
      AdobeFunctions.setPageName("1 inicio");
      set(window.digitalData, "page.pageInfo.level1", "cobrar jubilacion");
      set(window.digitalData, "page.pageInfo.level2", "1 inicio");
      set(
        window.digitalData,
        "application.step",
        get(window.digitalData, "page.pageInfo.level2")
      );

      // AdobeFunctions.setProductAttributes();
      // AdobeFunctions.setProductState("inicio");
      AdobeFunctions.callAdobeDTM("digitalView", "App Page Visit");
    });
  },
  thank(context) {
    AdobeFunctions.internal(context, () => {
      // Valores dinámicos de la página gracias
      //AdobeFunctions.clearVars();
      AdobeFunctions.setPageName("pagina exitosa");
      set(window.digitalData, "application.state", "finalizado");
      set(window.digitalData, "page.pageInfo.level2", "pagina exitosa");
      set(window.digitalData, "application.step", "pagina exitosa");
      set(window.digitalData, "application.state", "finalizado");
      AdobeFunctions.setProductAttributes();
      AdobeFunctions.setProductState("aprobado");
      AdobeFunctions.callAdobeDTM("digitalView", "App Completed");
    });
  }
};
export default Adobe;
